import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBaseAction } from "hooks/core";
import { API } from "constants/RestApi";
import { ITenant } from "components/tenants/types";
import BaseModal from "components/common/BaseModal/BaseModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab } from "react-bootstrap";
import useTenantAdmins from "hooks/tenant/useTenantAdmins";
import { IUser } from "core/types/base/IUser";
import ChangeUserEmailModal from "components/Admin/ChangeUserEmailModal/ChangeUserEmailModal";
import WarningModal from "components/common/Modals/WarningModal";
import AddEditAdminModal from "components/features/AddEditAdminModal";
import CompanyInfoTab from "components/tenants/CompanyInfoTab";
import ContactPersonTab from "components/tenants/ContactPersonTab";
import AdminsTab from "components/tenants/AdminsTab";
import CompanyInfoForm from "components/tenants/forms/CompanyInfoForm";
import ContactPersonForm from "components/tenants/forms/ContactPersonForm";
import FeaturesTab from "components/tenants/FeaturesTab";
import PageTitle from "components/common/Typography/PageTitle";
import TabItem from "components/common/Tab/TabItem";

const TenantDetailsScreen = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { request } = useBaseAction();
  const [tenant, setTenant] = useState<ITenant | null>(null);
  const [formData, setFormData] = useState<Partial<ITenant>>({});
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState<string>("");
  const [activeTab, setActiveTab] = useState("companyInfo");

  const {
    users,
    modals,
    toggleModal,
    createEditUser,
    handleConfirmUserAction,
    handleUserAction,
    getUsers,
    modalTitle,
    selectedEntity,
    changeUserEmail,
  } = useTenantAdmins({
    fetchOnLoad: true,
    tenantId: tenantId || "",
  });

  const { addUser, warningPassword, warningStatus, editUser, sendInvite, changeEmail } = modals;

  const fetchTenant = async () => {
    try {
      const response = await request({
        method: "GET",
        url: `${API.ADMIN_TENANTS}/${tenantId}`,
        useLoader: true,
      });
      setTenant(response);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch tenant details");
    }
  };

  const handleUpdate = async (updatedData: Partial<ITenant>) => {
    if (!tenant) return;
    try {
      await request({
        method: "PUT",
        url: API.ADMIN_TENANTS,
        body: {
          ...tenant,
          ...updatedData,
        },
        useLoader: true,
      });
      await fetchTenant();
      setShowModal(false);
      toast.success("Tenant details updated successfully!");
    } catch (error: any) {
      setModalError(error?.message || "Failed to update tenant details");
    }
  };

  const openEditModal = (tab: string) => {
    setActiveTab(tab);
    setShowModal(true);
    setModalError("");
    setFormData(tenant || {});
  };

  const closeModal = () => {
    setShowModal(false);
    setModalError("");
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (activeTab === "companyInfo") {
      setFormData(prev => ({
        ...prev,
        companyInfo: {
          ...(prev.companyInfo || {}),
          [name]: value || undefined,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        contactPerson: {
          ...(prev.contactPerson || {}),
          [name]: value || undefined,
        },
      }));
    }
  };

  const handleModalSave = async () => {
    if (!tenant) return;
    await handleUpdate(formData);
  };

  useEffect(() => {
    if (!tenantId) return;
    fetchTenant();
  }, [tenantId]);

  if (!tenant) {
    return <div>Loading...</div>;
  }

  const renderEditForm = () => {
    if (!tenant) return null;

    if (activeTab === "companyInfo") {
      return <CompanyInfoForm formData={formData} onChange={handleFormChange} />;
    }

    return <ContactPersonForm formData={formData} onChange={handleFormChange} />;
  };

  return (
    <div className="row">
      <PageTitle pageTitle={tenant.name || ""} shouldBackByDelta isBack />

      <div className="col-12">
        <ul className="nav nav-tabs mb-4">
          <TabItem
            value="companyInfo"
            label="Customer Info"
            activeTab={activeTab}
            onChange={setActiveTab}
          />
          <TabItem
            value="features"
            label="Features"
            activeTab={activeTab}
            onChange={setActiveTab}
          />
          <TabItem
            value="contactPerson"
            label="Contact Person"
            activeTab={activeTab}
            onChange={setActiveTab}
          />
          <TabItem value="admins" label="Admins" activeTab={activeTab} onChange={setActiveTab} />
        </ul>

        <Tab.Content>
          <Tab.Pane active={activeTab === "companyInfo"}>
            <CompanyInfoTab tenant={tenant} onEdit={() => openEditModal("companyInfo")} />
          </Tab.Pane>
          <Tab.Pane active={activeTab === "features"}>
            <FeaturesTab />
          </Tab.Pane>
          <Tab.Pane active={activeTab === "contactPerson"}>
            <ContactPersonTab tenant={tenant} onEdit={() => openEditModal("contactPerson")} />
          </Tab.Pane>
          <Tab.Pane active={activeTab === "admins"}>
            <AdminsTab
              users={users}
              onSort={getUsers}
              onAddAdmin={() => handleUserAction("addUser", {} as IUser)}
              handleDropdownAction={(row, event, actionKey) =>
                handleUserAction(actionKey, row, event)
              }
            />
          </Tab.Pane>
        </Tab.Content>
      </div>

      <BaseModal
        modalTitle={`Edit ${
          activeTab === "companyInfo" ? "Customer Information" : "Contact Person"
        }`}
        showModal={showModal}
        onClose={closeModal}
        onCancel={closeModal}
        onSave={handleModalSave}
        successBtnText="Save"
        cancelActionBtnText="Cancel"
        modalBody={
          <>
            {renderEditForm()}
            {modalError && <div className="alert alert-danger mt-3">{modalError}</div>}
          </>
        }
      />

      <AddEditAdminModal
        showModal={addUser || editUser}
        onCancel={() => toggleModal({ hideAll: true })}
        onClose={() => toggleModal({ hideAll: true })}
        onSave={user => user && createEditUser(user)}
        user={selectedEntity || ({} as IUser)}
        modalTitle={modalTitle}
        tenantId={tenantId}
      />

      <ChangeUserEmailModal
        showModal={changeEmail}
        onCancel={() => toggleModal({ hideAll: true })}
        onClose={() => toggleModal({ hideAll: true })}
        onSave={data => data && changeUserEmail(data)}
        user={selectedEntity || ({} as IUser)}
        modalTitle={modalTitle}
      />

      <WarningModal
        showModal={warningStatus || warningPassword || sendInvite}
        onClose={() => toggleModal({ hideAll: true })}
        onCancel={() => toggleModal({ hideAll: true })}
        onSave={() => handleConfirmUserAction()}
        modalTitle={modalTitle}
      />

      <ToastContainer />
    </div>
  );
};

export default TenantDetailsScreen;
